import { useEffect, useRef, useState } from 'react'
import {
  useCurrentCanvasSide,
  useDesignSide,
  useEditorConfig,
  useMultiColorMode,
} from '../EditorDataProvider'
import { useEditorFullScreenMode } from '../../UnifiedEditorProvider'
import { PatternWidget } from '../Widgets/PatternWidget'
import { mapProductSpecsToCanvas } from '../editor.utils'
import { LogoWidget } from '../Widgets/LogoWidget'
import {
  IEditorWidget,
  TDrillHoleLocation,
  TEdgeType,
  TProductShape,
} from '../editor.types'
import { productSizeToCutCornersSizeMapper } from '../editor-products.config'
import { INITIAL_BACKGROUND_COLOR } from '../editor.config'

export interface ICanvasColors {
  logoColor: {
    value: string
    label?: string
  }
  backgroundColor: {
    value: string
    label?: string
  }
}

const BLEED_LINE_WIDTH_PX = 0.3
const DRILL_HOLE_DIAMETER_MM = 3
const DRILL_HOLE_TOP_OFFSET_MM = 5

function CanvasDrillHole({
  drillHoleLocation,
  canvasWidth,
  canvasBleedHeight,
  canvasBleedWidth,
  productSizeToCanvasRatio,
}: {
  drillHoleLocation: TDrillHoleLocation
  canvasWidth: number
  canvasBleedHeight: number
  canvasBleedWidth: number
  productSizeToCanvasRatio: number
}) {
  const drillHoleRadius = DRILL_HOLE_DIAMETER_MM / productSizeToCanvasRatio / 2

  const locationToPositionMapper: Record<TDrillHoleLocation, number> = {
    'top left': canvasBleedWidth + drillHoleRadius * 2,
    'top middle': canvasWidth / 2,
    'top right': canvasWidth - canvasBleedWidth - drillHoleRadius * 2,
  }

  return (
    <circle
      id="drill-hole"
      cx={locationToPositionMapper[drillHoleLocation]}
      cy={
        canvasBleedHeight + DRILL_HOLE_TOP_OFFSET_MM / productSizeToCanvasRatio
      }
      strokeWidth={BLEED_LINE_WIDTH_PX}
      strokeDasharray="2,2"
      stroke="#FD566F"
      r={drillHoleRadius + 'px'}
      fill="white"
    />
  )
}

export type TCanvas = {
  shape: TProductShape
  width: number
  height: number
  bleedWidth: number
  bleedHeight: number
  canvasSvgData: string
  activeWidgetId: string
  activeWidgetType: string
  productSizeToCanvasRatio?: number
  widgets?: IEditorWidget[]
  edgeType?: TEdgeType
  drillHoleLocation?: TDrillHoleLocation
  safeZoneOffsets?: {
    top: number
    right: number
    left: number
    bottom: number
  }
} & ICanvasColors

export function Canvas({ className }: { className?: string }) {
  const { canvasConfig, updateCanvasConfig } = useCurrentCanvasSide()
  const { isFullScreen } = useEditorFullScreenMode()
  const editorConfig = useEditorConfig()
  const { multiColorModeIsOn } = useMultiColorMode()
  const { activeDesignSide } = useDesignSide()
  const canvasContainer = useRef(null)
  const canvasSvg = useRef(null)

  const [canvasContainerSize, setCanvasContainerSize] = useState({
    width: 0,
    height: 0,
  })

  const bleedLineOffsetX = canvasConfig?.bleedWidth - BLEED_LINE_WIDTH_PX
  const bleedLineOffsetY = canvasConfig?.bleedHeight - BLEED_LINE_WIDTH_PX

  useEffect(() => {
    if (canvasSvg?.current?.outerHTML) {
      updateCanvasConfig({
        canvasSvgData: canvasSvg?.current?.outerHTML,
      })
    }
  }, [canvasSvg?.current?.outerHTML])

  const updateCanvasContainerSize = () => {
    if (canvasContainer?.current) {
      setCanvasContainerSize({
        width: canvasContainer?.current?.parentNode?.clientWidth,
        height: canvasContainer?.current?.parentNode?.clientHeight,
      })
    }
  }

  useEffect(() => {
    window.addEventListener('resize', updateCanvasContainerSize)
    return () => window.removeEventListener('resize', updateCanvasContainerSize)
  }, [])

  useEffect(() => {
    updateCanvasContainerSize()
  }, [
    canvasContainer?.current?.parentNode?.clientWidth,
    canvasContainer?.current?.parentNode?.clientHeight,
    isFullScreen,
  ])

  useEffect(() => {
    // Recalculate the canvas's width and height base the rendered container size
    const newCanvasSize = mapProductSpecsToCanvas(
      editorConfig,
      canvasContainerSize
    )

    if (
      newCanvasSize?.width !== canvasConfig?.width ||
      newCanvasSize?.height !== canvasConfig?.height
    ) {
      updateCanvasConfig({
        ...newCanvasSize,
      })
    }
  })

  const generateBeveledTopShape = (
    bleedX = 0,
    bleedY = 0,
    width,
    height,
    beveledOffset = 5
  ) => {
    const cutSizeMm =
      productSizeToCutCornersSizeMapper[editorConfig?.productSlug][
        editorConfig?.size
      ] || beveledOffset
    const cutSizePx = cutSizeMm / canvasConfig?.productSizeToCanvasRatio

    return `M${bleedX} ${bleedY + cutSizePx}
    L${bleedX + cutSizePx} ${bleedY}
    L${width - bleedX - cutSizePx} ${bleedY}
    L${width - bleedX} ${bleedY + cutSizePx}
    L${width - bleedX} ${height - bleedY}
    L${bleedX} ${height - bleedY}
    Z`
  }

  const [backgroundColor, setBackgroundColor] = useState(
    canvasConfig?.backgroundColor?.value
  )

  useEffect(() => {
    setBackgroundColor(
      multiColorModeIsOn
        ? INITIAL_BACKGROUND_COLOR?.value
        : canvasConfig?.backgroundColor?.value
    )
  }, [multiColorModeIsOn, canvasConfig?.backgroundColor?.value])

  return (
    <div
      ref={canvasContainer}
      className={`absolute block -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 drop-shadow-lg ${
        canvasConfig.shape === 'circle' ? 'rounded-full' : ''
      } ${className}`}
      style={{
        borderRadius:
          canvasConfig.shape === 'oval'
            ? `${canvasConfig?.width / 2}px / ${canvasConfig?.height / 2}px`
            : '',
      }}
      key={activeDesignSide + String(isFullScreen)}
    >
      {editorConfig?.decorations?.includes('handles') && (
        <>
          <div
            className="absolute -top-8 h-8 w-[10%] left-[32%] z-10 border-[.5px] border-grey-10 bg-white"
            style={{
              backgroundColor: backgroundColor,
            }}
          />
          <div
            className="absolute -top-8 h-8 w-[10%] left-[58%] z-10 border-[.5px] border-grey-10 bg-white"
            style={{
              backgroundColor: backgroundColor,
            }}
          />
        </>
      )}

      {editorConfig?.decorations?.includes('side-pull') && (
        <>
          <div
            style={{
              height: Math.min(canvasConfig?.bleedHeight * 2, 20) + 'px',
              backgroundColor: backgroundColor,
            }}
            className="absolute top-0 w-[10%] right-full z-10 border-[.5px] border-grey-10 bg-white"
          />
          <div
            style={{
              height: Math.min(canvasConfig?.bleedHeight * 2, 20) + 'px',
              backgroundColor: backgroundColor,
            }}
            className="absolute top-0 w-[10%] left-full z-10 border-[.5px] border-grey-10 bg-white"
          />
        </>
      )}

      {editorConfig?.decorations?.includes('flap') && (
        <>
          <div
            style={{
              height: Math.min(canvasConfig?.bleedHeight * 2, 20) + 'px',
              backgroundColor: backgroundColor,
            }}
            className="absolute bottom-full w-full right-0 left-0 z-10 border-x-[.5px]"
          />
        </>
      )}

      {editorConfig?.decorations?.includes('left-flap') && (
        <>
          <div
            style={{
              width: Math.min(canvasConfig?.bleedWidth * 2, 16) + 'px',
              backgroundColor: backgroundColor,
            }}
            className="absolute right-full w-full top-0 bottom-0 z-10 border-x-[.5px]"
          />
        </>
      )}
      <svg
        id="canvasSvg"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        ref={canvasSvg}
        style={{
          width: canvasConfig.width + 'px',
          height: canvasConfig.height + 'px',
          zIndex: 20,
        }}
        width={canvasConfig.width}
        height={canvasConfig.height}
        clipPath="url(#clip)"
      >
        {/*
            Clip path that will be useful in MultiColor mode,
            when artwork is allowed to touch to trim line and safe zone
          */}

        <defs>
          <clipPath id="clip">
            {canvasConfig.shape === 'rectangle' && (
              <rect
                width={canvasConfig.width}
                height={canvasConfig.height}
                rx={canvasConfig?.edgeType === 'rounded' ? 10 : 0}
              />
            )}

            {canvasConfig.shape === 'circle' && (
              <circle
                cx={canvasConfig.width / 2}
                cy={canvasConfig.height / 2}
                r={canvasConfig.width / 2}
              />
            )}

            {canvasConfig.shape === 'oval' && (
              <ellipse
                cx={canvasConfig.width / 2}
                cy={canvasConfig.height / 2}
                rx={canvasConfig?.width / 2}
                ry={canvasConfig?.height / 2}
              />
            )}

            {canvasConfig.shape === 'beveled' && (
              <path
                d={generateBeveledTopShape(
                  0,
                  0,
                  canvasConfig?.width,
                  canvasConfig?.height,
                  Math.max(bleedLineOffsetX, bleedLineOffsetY)
                )}
              />
            )}
          </clipPath>
        </defs>

        {canvasConfig.shape === 'rectangle' && (
          <rect
            id="background-element"
            width={canvasConfig.width}
            height={canvasConfig.height}
            fill={backgroundColor}
            strokeWidth="1"
            stroke="#F2F2F2"
            rx={canvasConfig?.edgeType === 'rounded' ? 10 : 0}
          />
        )}

        {canvasConfig.shape === 'circle' && (
          <circle
            id="background-element"
            cx={canvasConfig.width / 2}
            cy={canvasConfig.height / 2}
            strokeDasharray="6,3"
            strokeWidth="1"
            stroke="#F2F2F2"
            r={canvasConfig.width / 2}
            fill={backgroundColor}
          />
        )}

        {canvasConfig.shape === 'oval' && (
          <ellipse
            id="background-element"
            cx={canvasConfig.width / 2}
            cy={canvasConfig.height / 2}
            strokeWidth="1"
            stroke="#F2F2F2"
            rx={canvasConfig?.width / 2}
            ry={canvasConfig?.height / 2}
            fill={backgroundColor}
          />
        )}

        {canvasConfig.shape === 'beveled' && (
          <path
            id="background-element"
            stroke="#F2F2F2"
            strokeWidth="1"
            fill={backgroundColor}
            d={generateBeveledTopShape(
              0,
              0,
              canvasConfig?.width,
              canvasConfig?.height,
              Math.max(bleedLineOffsetX, bleedLineOffsetY)
            )}
          />
        )}

        {canvasConfig?.widgets?.length &&
          canvasConfig?.widgets?.map((widget) => {
            if (widget.type === 'logo') {
              return (
                <LogoWidget
                  key={widget.id || 'dummy-id'}
                  position={widget.config.position}
                  positionKey={widget.config.positionKey}
                  size={widget.config.size}
                  rotation={widget.config.rotation}
                  assetSvgData={widget?.config?.assetSvgData}
                  color={canvasConfig.logoColor?.value}
                  scaleStepCount={widget?.config?.scaleStepCount}
                  assetUrl={widget?.config?.assetUrl}
                  multiColorMode={multiColorModeIsOn}
                />
              )
            } else if (widget.type === 'pattern') {
              return (
                <PatternWidget
                  key={widget.id}
                  {...widget.config}
                  color={canvasConfig.logoColor?.value}
                  multiColorMode={multiColorModeIsOn}
                  assetUrl={widget?.config?.assetUrl}
                />
              )
            }
            return null
          })}

        {canvasConfig?.drillHoleLocation && (
          <CanvasDrillHole
            canvasWidth={canvasConfig?.width}
            canvasBleedHeight={canvasConfig?.bleedHeight}
            canvasBleedWidth={canvasConfig?.bleedWidth}
            drillHoleLocation={canvasConfig?.drillHoleLocation}
            productSizeToCanvasRatio={canvasConfig?.productSizeToCanvasRatio}
          />
        )}

        {canvasConfig?.safeZoneOffsets?.left &&
          canvasConfig.shape === 'rectangle' && (
            <rect
              id="safe-zone-line"
              width={
                canvasConfig?.width -
                canvasConfig?.safeZoneOffsets?.left * 2 -
                canvasConfig?.bleedWidth * 2
              }
              height={
                canvasConfig.height -
                (canvasConfig?.safeZoneOffsets.bottom +
                  canvasConfig?.safeZoneOffsets?.top) -
                canvasConfig?.bleedHeight * 2
              }
              rx={canvasConfig?.edgeType === 'rounded' ? 10 : 0}
              x={canvasConfig?.safeZoneOffsets?.left + canvasConfig?.bleedWidth}
              y={canvasConfig?.safeZoneOffsets?.top + canvasConfig?.bleedHeight}
              strokeWidth={BLEED_LINE_WIDTH_PX}
              strokeDasharray="6,3"
              stroke="blue"
              fill="transparent"
            />
          )}

        {canvasConfig?.bleedWidth && canvasConfig.shape === 'rectangle' && (
          <rect
            id="trim-line"
            width={canvasConfig.width - canvasConfig?.bleedWidth * 2}
            height={canvasConfig.height - canvasConfig?.bleedHeight * 2}
            rx={canvasConfig?.edgeType === 'rounded' ? 10 : 0}
            x={canvasConfig?.bleedWidth}
            y={canvasConfig?.bleedHeight}
            strokeWidth={BLEED_LINE_WIDTH_PX}
            strokeDasharray="6,3"
            stroke="#FD566F"
            fill="transparent"
          />
        )}

        {canvasConfig?.bleedWidth && canvasConfig.shape === 'circle' && (
          <circle
            id="trim-line"
            cx={canvasConfig.width / 2}
            cy={canvasConfig.height / 2}
            strokeWidth={BLEED_LINE_WIDTH_PX}
            strokeDasharray="6,3"
            stroke="#FD566F"
            r={canvasConfig.width / 2 - canvasConfig.bleedWidth}
            fill="none"
          />
        )}

        {canvasConfig?.bleedWidth && canvasConfig.shape === 'oval' && (
          <ellipse
            id="trim-line"
            cx={canvasConfig.width / 2}
            cy={canvasConfig.height / 2}
            strokeWidth={BLEED_LINE_WIDTH_PX}
            strokeDasharray="6,3"
            stroke="#FD566F"
            rx={canvasConfig.width / 2 - canvasConfig.bleedWidth}
            ry={canvasConfig.height / 2 - canvasConfig?.bleedHeight}
            fill="none"
          />
        )}

        {canvasConfig?.bleedWidth && canvasConfig.shape === 'beveled' && (
          <path
            id="trim-line"
            fill="transparent"
            strokeWidth={BLEED_LINE_WIDTH_PX}
            stroke="#FD566F"
            strokeLinecap="butt"
            strokeLinejoin="round"
            strokeDasharray="6,3"
            d={generateBeveledTopShape(
              bleedLineOffsetX,
              bleedLineOffsetY,
              canvasConfig?.width,
              canvasConfig?.height,
              Math.max(bleedLineOffsetX, bleedLineOffsetY)
            )}
          />
        )}
      </svg>
    </div>
  )
}
